import { FunctionComponent, useEffect, useState } from 'react';
import usePostBulkPayment from '../../../../hooks/useApi/usePostBulkPayment';
import usePostBulkCollection from '../../../../hooks/useApi/usePostBulkCollection';
import { BatchTransactionDialogueHandler, DialogueHandlerProps } from '../../Dialogue/types';
import BatchTransactionDialogueContext, {
  BatchTransactionCtx,
} from '../../Dialogue/BatchTransaction/BatchTransactionContext';
import BatchTransactionDialogue from '../../Dialogue/BatchTransaction/BatchTransactionDialogue';
import { useTranslation } from 'react-i18next';

import { Box, Table, Button, formattedCurrency, TableProps, ContactGroupsLists } from 'vopay';

type ContactListType = {
  [key: number]: {
    Account: string;
    Amount: string;
    ClientReferenceNumber: string;
    Currency: string;
    FirstName: string;
    IsActive: string;
    LastName: string;
    Message: string;
    Token: string;
    AccountHolderName: string;
    ContactID: string;
  };
};

type ContactTableProps = {
  groupList: ContactListType;
  group: ContactGroupsLists;
  type: string;
  handleReview: (e: boolean) => void;
  handleTransactionDetail: (e: boolean) => void;
  handleList: (list: ContactListType) => void;
};

type FC = FunctionComponent<ContactTableProps>;

const BulkTransactionReviewTable: FC = (props: ContactTableProps) => {
  const { groupList, group, type, handleTransactionDetail, handleList } = props;
  const [bulkPaymentResponse, postBulkPayment] = usePostBulkPayment({});
  const [bulkCollectionResponse, postBulkCollection] = usePostBulkCollection({});
  const [shouldPostBulkTransaction, setShouldPostBulkTransaction] = useState(false);
  const [showBatchTransactionErrorAlert, setShowBatchTransactionErrorAlert] = useState(false);
  const [batchTransactionError, setBatchTransactionError] = useState('');
  const [filteredList, setFilteredList] = useState<ContactListType>({});
  const { t } = useTranslation('translations', { keyPrefix: 'Groups.Details.Contacts.Table' });

  const BatchTransaction: BatchTransactionCtx = {
    showBatchTransactionErrorAlert,
    batchTransactionError,
  };

  const handlers = {
    handleDeleteGroupDialogue: (handlerProps: DialogueHandlerProps<BatchTransactionDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;
      const dialogueHandlers: BatchTransactionDialogueHandler = {
        error: {
          confirm: {
            default: () => {
              setShowBatchTransactionErrorAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;
      handler();
    },
  };

  useEffect(() => {
    const filteredList2 = Object.values(groupList)
      .filter((contact) => Number(contact.Amount) > 0)
      .reduce(
        (accumulator, contact, index) => ({
          ...accumulator,
          [index]: {
            Account: contact.Account,
            ClientReferenceNumber: contact.ClientReferenceNumber,
            IsActive: contact.IsActive,
            Token: contact.Token,
            Amount: contact.Amount,
            Currency: contact.Currency,
            FirstName: contact.FirstName,
            LastName: contact.LastName,
            Message: contact.Message,
          },
        }),
        {},
      );
    setFilteredList(filteredList2);
    handleList(filteredList2);
  }, []);

  // batch eft withdraw / fund
  useEffect(() => {
    if (shouldPostBulkTransaction) {
      if (type === 'Payment') {
        postBulkPayment({
          BatchName: group.GroupName,
          BatchData: JSON.stringify(Object.values(filteredList)).replace(/\'/g, "\\\\'"),
        });
      } else {
        postBulkCollection({
          BatchName: group.GroupName,
          BatchData: JSON.stringify(Object.values(filteredList)).replace(/\'/g, "\\\\'"),
        });
      }
    }
  }, [shouldPostBulkTransaction]);

  useEffect(() => {
    if (shouldPostBulkTransaction) {
      if (type === 'Payment') {
        const { Success, ErrorMessage } = bulkPaymentResponse;

        if (Success === true) {
          handleTransactionDetail(true);
        } else if (Success === false) {
          setShowBatchTransactionErrorAlert(true);
          setBatchTransactionError(ErrorMessage);
        }
      } else {
        const { Success, ErrorMessage } = bulkCollectionResponse;

        if (Success === true) {
          handleTransactionDetail(true);
        } else if (Success === false) {
          setShowBatchTransactionErrorAlert(true);
          setBatchTransactionError(ErrorMessage);
        }
      }
    }
    setShouldPostBulkTransaction(false);
  }, [bulkPaymentResponse, bulkCollectionResponse]);

  const reviewTableProps: TableProps = {
    name: 'review-bulk-transaction-list',
    columns: {
      contact: { header: t('contactName'), align: 'left' },
      account: { header: t('account'), align: 'left' },
      amount: { header: t('amount'), align: 'left' },
      reference: { header: t('reference'), align: 'left' },
      service: { header: t('service'), align: 'left' },
    },
    rows:
      Object.values(filteredList).map((contact) => ({
        contact: <>{`${contact.FirstName} ${contact.LastName}`}</>,
        account: contact.Account,
        amount: <>{`$${formattedCurrency(contact.Amount)} ${group.ACTCurrencies_ID === '1' ? 'USD' : 'CAD'} `}</>,
        reference: contact.ClientReferenceNumber,
        service: <>{type === 'Payment' ? t('Row.Service.credit') : t('Row.Service.debit')}</>,
      })) || [],
  };

  return (
    <BatchTransactionDialogueContext.Provider value={BatchTransaction}>
      <Table {...reviewTableProps} />
      <Box width="100%" jc="fe" mt="md">
        <Button
          id="submit-bulk-transaction"
          label="Submit"
          width="140px"
          noShadow
          type="submit"
          onClick={() => {
            setShouldPostBulkTransaction(true);
          }}
        />
      </Box>
      <BatchTransactionDialogue handleDialogue={handlers.handleDeleteGroupDialogue} />
    </BatchTransactionDialogueContext.Provider>
  );
};

export default BulkTransactionReviewTable;
